import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest,
  buildTranslateAdminProspectLetterTemplateToEnglishRequest,
  buildUploadAdminProspectLetterTemplateAssetRequest,
  generateAdminProspectLetterTemplateOperationId,
  getAdminProspectLetterTemplateAssetPreviewUrl,
  translateAdminProspectLetterTemplateToEnglish,
  uploadAdminProspectLetterTemplateAsset,
} from '../../../services/admin/prospectLetterTemplatesService.js';
import { cloneCallableSafeLetterTemplateJson } from '../../../services/admin/letterTemplateCallableSafety.js';
import LetterTemplateEditor from './LetterTemplateEditor.jsx';
import {
  countCombinedLetterTemplateImageNodes,
  countCombinedLetterTemplateQrBlocks,
  createEmptyLetterTemplateDocument,
  extractLetterTemplateAssetIds,
} from './letterTemplateDocument.js';
import { shouldConfirmDisableBilingual } from './letterTemplateBilingualState.js';
import {
  createTranslationRequestGuard,
  initializeTranslationSession,
  isTranslationStale,
  shouldApplyInFlightTranslationResult,
  shouldConfirmBeforeReplaceEnglish,
  usesRetranslateButtonLabel,
  validateTranslationResponseAgainstGuard,
} from './letterTemplateTranslationState.js';
import {
  fileToRawBase64,
  resolveImageUploadAttempt,
  validateLetterTemplateImageFile,
} from './letterTemplateImageUpload.js';
import { useLetterTemplateAssetPreviewUrls, readLetterTemplateAssetPreviewUrl } from './useLetterTemplateAssetPreviewUrls.js';

/**
 * @param {{
 *   frenchDocument: object,
 *   englishDocument?: object | null,
 *   bilingual: boolean,
 *   translation?: object | null,
 *   editorSessionKey: string,
 *   readOnly?: boolean,
 *   impersonating?: boolean,
 *   t: (key: string) => string,
 *   onFrenchDocumentChange: (doc: object) => void,
 *   onEnglishDocumentChange?: (doc: object) => void,
 *   onBilingualChange?: (bilingual: boolean) => void,
 *   onTranslationChange?: (translation: object | null, sourceSnapshot: object | null) => void,
 * }} props
 */
export default function LetterTemplateRichEditorShell({
  frenchDocument,
  englishDocument,
  bilingual,
  translation,
  editorSessionKey,
  readOnly = false,
  impersonating = false,
  t,
  onFrenchDocumentChange,
  onEnglishDocumentChange,
  onBilingualChange,
  onTranslationChange,
}) {
  const [frQrCount, setFrQrCount] = useState(0);
  const [enQrCount, setEnQrCount] = useState(0);
  const [translationMetadata, setTranslationMetadata] = useState(translation || null);
  const [translationSourceSnapshot, setTranslationSourceSnapshot] = useState(null);
  const [lastTranslatedEnglishSnapshot, setLastTranslatedEnglishSnapshot] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translationErrorKey, setTranslationErrorKey] = useState(null);
  const [imageErrorKey, setImageErrorKey] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [failedUploadAttempt, setFailedUploadAttempt] = useState(null);
  const [previewRetryAssetId, setPreviewRetryAssetId] = useState(null);
  const frEditorRef = useRef(null);
  const enEditorRef = useRef(null);

  const frenchDocumentRef = useRef(frenchDocument);
  const englishDocumentRef = useRef(englishDocument);
  const bilingualRef = useRef(bilingual);
  const editorSessionKeyRef = useRef(editorSessionKey);
  const componentAliveRef = useRef(true);
  const translationRequestIdRef = useRef(0);

  useEffect(() => {
    componentAliveRef.current = true;
    return () => {
      componentAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    frenchDocumentRef.current = frenchDocument;
  }, [frenchDocument]);
  useEffect(() => {
    englishDocumentRef.current = englishDocument;
  }, [englishDocument]);
  useEffect(() => {
    bilingualRef.current = bilingual;
  }, [bilingual]);
  useEffect(() => {
    editorSessionKeyRef.current = editorSessionKey;
  }, [editorSessionKey]);

  useEffect(() => {
    const session = initializeTranslationSession(frenchDocument, translation, englishDocument);
    setTranslationMetadata(session.metadata);
    setTranslationSourceSnapshot(session.sourceSnapshot);
    setLastTranslatedEnglishSnapshot(session.lastTranslatedEnglishSnapshot);
  }, [translation]);

  const previewEnabled = !impersonating;

  const fetchPreviewUrl = useCallback(async (assetId) => {
    const result = await getAdminProspectLetterTemplateAssetPreviewUrl(
      buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest({ assetId }),
    );
    return result;
  }, []);

  const { getPreviewState, requestPreview, invalidate } = useLetterTemplateAssetPreviewUrls({
    fetchPreviewUrl,
    enabled: previewEnabled,
  });

  const getImagePreviewUrl = useCallback(
    (assetId) => readLetterTemplateAssetPreviewUrl(getPreviewState, assetId, { impersonating }),
    [getPreviewState, impersonating],
  );

  const retryImagePreview = useCallback(
    (assetId) => {
      if (!assetId || impersonating) return;
      setPreviewRetryAssetId(assetId);
      setImageErrorKey(null);
      invalidate(assetId);
      requestPreview(assetId, { force: true }).catch(() => {
        setImageErrorKey('admin.prospection.letterTemplate.error.previewUnavailable');
      });
    },
    [impersonating, invalidate, requestPreview],
  );

  const allAssetIds = useMemo(() => {
    const ids = new Set([
      ...extractLetterTemplateAssetIds(frenchDocument),
      ...(bilingual ? extractLetterTemplateAssetIds(englishDocument) : []),
    ]);
    return [...ids];
  }, [frenchDocument, englishDocument, bilingual]);

  useEffect(() => {
    if (!previewEnabled) return;
    for (const assetId of allAssetIds) {
      requestPreview(assetId).catch(() => {});
    }
  }, [allAssetIds, previewEnabled, requestPreview]);

  const combinedQr = countCombinedLetterTemplateQrBlocks(frenchDocument, bilingual ? englishDocument : null);
  const combinedImages = countCombinedLetterTemplateImageNodes(
    frenchDocument,
    bilingual ? englishDocument : null,
  );
  const showBilingualQrNote = bilingual && combinedQr >= 1;
  const imageInsertDisabled = combinedImages >= 6 || uploadInProgress;
  const frQrDisabled = combinedQr >= 1 && enQrCount > 0;
  const enQrDisabled = combinedQr >= 1 && frQrCount > 0;
  const translationStale = isTranslationStale(
    frenchDocument,
    translationMetadata,
    translationSourceSnapshot,
  );

  const handleFrenchChange = (doc) => {
    onFrenchDocumentChange(doc);
    if (translationMetadata) {
      setTranslationErrorKey(null);
    }
  };

  const handleBilingualToggle = (checked) => {
    if (checked) {
      onBilingualChange?.(true);
      if (!englishDocument) {
        onEnglishDocumentChange?.(createEmptyLetterTemplateDocument());
      }
      return;
    }
    if (shouldConfirmDisableBilingual(englishDocument)) {
      const ok = window.confirm(t('admin.prospection.letterTemplate.bilingual.disableConfirm'));
      if (!ok) return;
    }
    onBilingualChange?.(false);
    onTranslationChange?.(null, null);
    setTranslationMetadata(null);
    setTranslationSourceSnapshot(null);
    setLastTranslatedEnglishSnapshot(null);
  };

  const handleTranslate = async () => {
    if (translating || readOnly || impersonating) return;
    if (shouldConfirmBeforeReplaceEnglish(englishDocumentRef.current)) {
      const ok = window.confirm(t('admin.prospection.letterTemplate.translation.retranslateConfirm'));
      if (!ok) return;
    }

    const requestGuard = createTranslationRequestGuard({
      frenchSnapshot: cloneCallableSafeLetterTemplateJson(frenchDocumentRef.current),
      englishSnapshot: cloneCallableSafeLetterTemplateJson(englishDocumentRef.current),
      bilingual: bilingualRef.current,
      editorSessionKey: editorSessionKeyRef.current,
    });

    const requestId = ++translationRequestIdRef.current;

    setTranslating(true);
    setTranslationErrorKey(null);
    try {
      const response = await translateAdminProspectLetterTemplateToEnglish(
        buildTranslateAdminProspectLetterTemplateToEnglishRequest({
          document: requestGuard.frenchSnapshot,
        }),
      );
      if (
        !shouldApplyInFlightTranslationResult({
          componentAlive: componentAliveRef.current,
          requestId,
          activeRequestId: translationRequestIdRef.current,
        })
      ) {
        return;
      }
      const resolved = validateTranslationResponseAgainstGuard(requestGuard, {
        frenchDocument: frenchDocumentRef.current,
        englishDocument: englishDocumentRef.current,
        bilingual: bilingualRef.current,
        editorSessionKey: editorSessionKeyRef.current,
        response,
      });
      if (!resolved.ok) {
        setTranslationErrorKey('admin.prospection.letterTemplate.translation.raceWarning');
        return;
      }
      onEnglishDocumentChange?.(resolved.englishDocument);
      setTranslationMetadata(resolved.metadata);
      setTranslationSourceSnapshot(resolved.sourceSnapshot);
      setLastTranslatedEnglishSnapshot(resolved.lastTranslatedEnglishSnapshot);
      onTranslationChange?.(resolved.metadata, resolved.sourceSnapshot);
    } catch {
      if (
        !shouldApplyInFlightTranslationResult({
          componentAlive: componentAliveRef.current,
          requestId,
          activeRequestId: translationRequestIdRef.current,
        })
      ) {
        return;
      }
      setTranslationErrorKey('admin.prospection.letterTemplate.error.translationUnavailable');
    } finally {
      if (
        shouldApplyInFlightTranslationResult({
          componentAlive: componentAliveRef.current,
          requestId,
          activeRequestId: translationRequestIdRef.current,
        })
      ) {
        setTranslating(false);
      }
    }
  };

  const insertImageNode = useCallback((lang, attrs) => {
    if (lang === 'en') {
      enEditorRef.current?.insertImage?.(attrs);
    } else {
      frEditorRef.current?.insertImage?.(attrs);
    }
  }, []);

  const executeImageUpload = async (attempt) => {
    setUploadInProgress(true);
    setImageErrorKey(null);
    setPreviewRetryAssetId(null);
    try {
      const base64 = await fileToRawBase64(attempt.file);
      const uploadResult = await uploadAdminProspectLetterTemplateAsset(
        buildUploadAdminProspectLetterTemplateAssetRequest({
          operationId: attempt.operationId,
          contentType: attempt.file.type,
          base64,
        }),
      );
      const assetId = uploadResult?.assetId;
      if (!assetId) throw new Error('missing_asset_id');

      const attrs = {
        assetId,
        widthPct: 50,
        align: 'center',
        alt: '',
      };
      insertImageNode(attempt.language, attrs);
      setFailedUploadAttempt(null);

      try {
        await requestPreview(assetId);
      } catch {
        setPreviewRetryAssetId(assetId);
        setImageErrorKey('admin.prospection.letterTemplate.error.previewUnavailable');
      }
    } catch {
      setFailedUploadAttempt(attempt);
      setImageErrorKey('admin.prospection.letterTemplate.error.imageUploadFailed');
    } finally {
      setUploadInProgress(false);
    }
  };

  const processImageUpload = async (file, lang) => {
    if (uploadInProgress) return;
    const validation = validateLetterTemplateImageFile(file);
    if (validation === 'invalid_mime') {
      setImageErrorKey('admin.prospection.letterTemplate.error.invalidImageMime');
      return;
    }
    if (validation === 'too_large') {
      setImageErrorKey('admin.prospection.letterTemplate.error.imageTooLarge');
      return;
    }

    const attempt = resolveImageUploadAttempt(
      failedUploadAttempt,
      file,
      lang,
      generateAdminProspectLetterTemplateOperationId,
    );
    await executeImageUpload(attempt);
  };

  const handleRetryUpload = async () => {
    if (!failedUploadAttempt || uploadInProgress) return;
    await executeImageUpload(failedUploadAttempt);
  };

  const handleRetryPreview = () => {
    if (!previewRetryAssetId) return;
    retryImagePreview(previewRetryAssetId);
  };

  const translateLabel = usesRetranslateButtonLabel(Boolean(translationMetadata))
    ? t('admin.prospection.letterTemplate.translation.retranslate')
    : t('admin.prospection.letterTemplate.translation.translate');

  return (
    <div className="space-y-6">
      {!readOnly ? (
        <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={bilingual}
            onChange={(e) => handleBilingualToggle(e.target.checked)}
            disabled={impersonating}
          />
          {t('admin.prospection.letterTemplate.bilingual.toggle')}
        </label>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-800">
          {t('admin.prospection.letterTemplate.section.french')}
        </h2>
        <LetterTemplateEditor
          ref={frEditorRef}
          letterDocument={frenchDocument}
          readOnly={readOnly}
          t={t}
          onDocumentChange={handleFrenchChange}
          onQrCountChange={setFrQrCount}
          imageInsertDisabled={imageInsertDisabled}
          imageUploading={uploadInProgress}
          onImageFileSelected={(file) => processImageUpload(file, 'fr')}
          getImagePreviewUrl={getImagePreviewUrl}
          retryImagePreview={retryImagePreview}
          qrInsertDisabled={frQrDisabled}
          showBilingualQrNote={showBilingualQrNote}
        />
      </section>

      {bilingual ? (
        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-800">
              {t('admin.prospection.letterTemplate.section.english')}
            </h2>
            {!readOnly && !impersonating ? (
              <button
                type="button"
                onClick={handleTranslate}
                disabled={translating}
                className="rounded-lg border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                {translating
                  ? t('admin.prospection.letterTemplate.translation.translating')
                  : translateLabel}
              </button>
            ) : null}
          </div>
          {translationStale ? (
            <p className="mb-2 text-sm text-amber-800" role="status">
              {t('admin.prospection.letterTemplate.translation.staleWarning')}
            </p>
          ) : null}
          {translationErrorKey ? (
            <p className="mb-2 text-sm text-rose-800" role="alert">
              {t(translationErrorKey)}
            </p>
          ) : null}
          <LetterTemplateEditor
            ref={enEditorRef}
            letterDocument={englishDocument || createEmptyLetterTemplateDocument()}
            readOnly={readOnly}
            t={t}
            onDocumentChange={onEnglishDocumentChange}
            onQrCountChange={setEnQrCount}
            qrInsertDisabled={enQrDisabled}
            imageInsertDisabled={imageInsertDisabled}
            imageUploading={uploadInProgress}
            onImageFileSelected={(file) => processImageUpload(file, 'en')}
            getImagePreviewUrl={getImagePreviewUrl}
            retryImagePreview={retryImagePreview}
            showBilingualQrNote={showBilingualQrNote}
          />
        </section>
      ) : null}

      {imageErrorKey ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          <p>{t(imageErrorKey)}</p>
          {failedUploadAttempt ? (
            <button
              type="button"
              className="mt-2 font-medium underline"
              onClick={handleRetryUpload}
              disabled={uploadInProgress}
            >
              {t('admin.prospection.letterTemplate.image.retryUpload')}
            </button>
          ) : null}
          {previewRetryAssetId && !failedUploadAttempt ? (
            <button
              type="button"
              className="mt-2 font-medium underline"
              onClick={handleRetryPreview}
            >
              {t('admin.prospection.letterTemplate.image.retryPreview')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export {
  isTranslationStale,
};
